import React, { FunctionComponent, useEffect, useState } from 'react'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { SelectField } from 'components/elements/SelectField'
import { useTranslation } from 'react-i18next'
import { useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import { useSubscriptionOffers } from 'SubscriptionOffers/SubscriptionOffersProvider'
import { TextField } from 'components/elements/TextField'
import { DateField } from 'components/elements/DateField'
import { format } from 'date-fns'
import { formatDateFieldToDate, parseDateField, yupDateField } from 'utils'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { Checkbox } from '@material-ui/core'
import { Shape } from 'yup'

type Props = {
  open: boolean
}
type Functions = {
  setOpen(value: boolean): void
}
type AllProps = Props & Functions

const AddSubscriptionOfferDialog: FunctionComponent<AllProps> = ({ open, setOpen }) => {
  const { t, i18n } = useTranslation()
  const api = useApi()
  const { addZones: zones, setLoadList } = useSubscriptionOffers()
  const { showSuccess, showError } = useMessage()

  const [typeOptions, setTypeOptions] = useState([])

  const handleClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (zones.length == 1) {
      formal.change('zone', zones[0].id)
    }
  }, [zones])

  const initialValues: Shape<object, {
    zone: number
    type: number
    price: number | '0.00'
    validFrom: string
    validTo: string
    maxActiveSubscriptions: number | ''
    maxActiveSessions: number | ''
    autoRenew: boolean
    allClients: boolean
    client: string
  }> = {
    zone: 0,
    type: 0,
    price: '0.00',
    validFrom: format(new Date, 'dd.MM.yyyy'),
    validTo: '',
    maxActiveSubscriptions: '',
    maxActiveSessions: '',
    autoRenew: false,
    allClients: false,
    client: '',
  }

  const schema = yup.object().shape({
    zone: yup.number().required().min(1),
    type: yup.number().required().min(1),
    price: yup.number().required().min(0).test({
      test: value => {
        return (Math.round(value * 100) / 100) == value
      },
    }),
    validFrom: yupDateField({ required: true }),
    validTo: yupDateField(),
    maxActiveSubscriptions: yup.lazy(value =>
      value === ''
        ? yup.string()
        : yup.number().required().integer().min(1),
    ),
    maxActiveSessions: yup.lazy(value =>
      value === ''
        ? yup.string()
        : yup.number().required().integer().min(1),
    ),
    autoRenew: yup.boolean(),
    allClients: yup.boolean(),
    client: yup.string().when('allClients', {
      is: false,
      then: schema => schema.trim().matches(/^[0-9a-zA-Z.]{0,30}$/),
    }),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.createSubscriptionOffer({
        type_id: values.type,
        price: Math.round(Number(values.price) * 100),
        valid_from: formatDateFieldToDate(values.validFrom),
        valid_to: values.validTo ? formatDateFieldToDate(values.validTo) : undefined,
        max_active_subscriptions: values.maxActiveSubscriptions ? values.maxActiveSubscriptions : undefined,
        max_active_sessions: values.maxActiveSessions ? values.maxActiveSessions : undefined,
        auto_renew: Number(values.price) && values.autoRenew ? true : undefined,
        client: values.allClients ? null : values.client || undefined,
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          handleClose()

          if (zones.length > 1) {
            formal.change('zone', initialValues.zone)
          } else if (typeOptions.length > 1) {
            formal.change('type', initialValues.type)
          }
          formal.change('price', initialValues.price)
          formal.change('validFrom', initialValues.validFrom)
          formal.change('validTo', initialValues.validTo)
          formal.change('maxActiveSubscriptions', initialValues.maxActiveSubscriptions)
          formal.change('maxActiveSessions', initialValues.maxActiveSessions)
          formal.change('autoRenew', initialValues.autoRenew)
          formal.change('allClients', initialValues.allClients)
          formal.change('client', initialValues.client)

          setLoadList()

          showSuccess(t('messages.subscriptionOfferCreated'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotCreateSubscriptionOffer')} ${message}`)
        })
    },
  })

  useEffect(() => {
    const typeOptions = ((formal.values.zone && (zones.find(z => z.id == formal.values.zone).types)) || []).map(type => ({
      label: type.name,
      id: type.id,
    }))

    setTypeOptions(typeOptions)

    formal.change('type', typeOptions.length == 1 ? typeOptions[0].id : 0)
  }, [formal.values.zone])

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('newSubscriptionOffer')}</DraggableDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <SelectField
              GridProps={{ xs: 12 }}
              label={t('zone')}
              required
              optionsCustom={[
                {
                  label: zones.length > 1 ? t('select') : '',
                  id: 0,
                },
                ...zones.sort((a, b) => a.code.localeCompare(b.code)).map(zone => {
                  const localization = zone.localizations.find(l => l.language == i18n.language)

                  return {
                    label: `${zone.code}${localization && localization.name ? ` ${localization.name}` : ''}`,
                    id: zone.id,
                  }
                }),
              ]}
              typeahead
              {...formal.getFieldProps('zone')}
              disabled={zones.length <= 1 || formal.isSubmitting}
            />
            <SelectField
              GridProps={{ xs: 12 }}
              label={t('type')}
              required
              optionsCustom={[
                {
                  label: typeOptions.length > 1 ? t('select') : '...',
                  id: 0,
                },
                ...typeOptions,
              ]}
              {...formal.getFieldProps('type')}
              disabled={typeOptions.length <= 1 || formal.isSubmitting}
            />
            <TextField
              GridProps={{ xs: 12 }}
              label={t('price')}
              required
              {...formal.getFieldProps('price')}
            />
            <DateField
              GridProps={{ xs: 6 }}
              label={t('startDate')}
              required
              {...(formal.values.validTo && { maxDate: parseDateField(formal.values.validTo) })}
              {...formal.getFieldProps('validFrom')}
            />
            <DateField
              GridProps={{ xs: 6 }}
              label={t('endDate')}
              clearable
              {...(formal.values.validFrom && { minDate: parseDateField(formal.values.validFrom) })}
              {...formal.getFieldProps('validTo')}
            />
            <TextField
              GridProps={{ xs: 6 }}
              type="number"
              label={t('numberOfSubscriptions')}
              inputProps={{
                min: 1,
              }}
              helperText={t('maxActiveSubscriptions')}
              {...formal.getFieldProps('maxActiveSubscriptions')}
            />
            <TextField
              GridProps={{ xs: 6 }}
              type="number"
              label={t('numberOfSessions')}
              inputProps={{
                min: 1,
              }}
              helperText={t('maxActiveSessions')}
              {...formal.getFieldProps('maxActiveSessions')}
            />
            <Grid item xs={12} style={{ paddingLeft: 19 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    {...formal.getFieldProps('autoRenew')}
                    checked={formal.values.autoRenew}
                    onChange={(event) => formal.change('autoRenew', event.target.checked)}
                    disabled={!Number(formal.values.price)}
                  />
                }
                label={t('withAutoRenew')}
              />
            </Grid>
            <Grid item xs={12} style={{ paddingLeft: 19 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    {...formal.getFieldProps('allClients')}
                    checked={formal.values.allClients}
                    onChange={event => formal.change('allClients', event.target.checked)}
                  />
                }
                label={t('allClients')}
              />
            </Grid>
            <TextField
              GridProps={{ xs: 12 }}
              label={t('client')}
              helperText={t('clientPhoneNumberOrUsernameHelperText')}
              {...formal.getFieldProps('client')}
              disabled={formal.values.allClients}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formal.isSubmitting}
          >
            {t('add')}
          </Button>
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default AddSubscriptionOfferDialog
